import {
  Button,
  Checkbox,
  Divider,
  LoadingOverlay,
  Menu,
  Text,
  Tooltip,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { motion } from "framer-motion"
import { t } from "i18next"
import { debounce } from "lodash"
import {
  AlertTriangle,
  DollarSign,
  FileSpreadsheet,
  Package,
  Plus,
  Sliders,
  TrendingUp,
  XCircle,
} from "lucide-react"
import { useEffect, useState } from "react"
import { Link, useLocation, useParams } from "react-router-dom"
import {
  CollectionQuery,
  Filter,
  Order,
} from "../../../models/collection.model"
import {
  EntityConfig,
  entityViewMode,
} from "../../../models/entity-config.model"
import {
  Lease,
  LeaseStatus,
  PaymentFrequency,
} from "../../../models/lease.model"
import EntityList from "../../../shared/component/entity-list/entity-list.component"
import { useCurrentUser } from "../../../shared/hooks/current-user.hook"
import { dateFormat } from "../../../shared/utility/date-format/date-format"
import { formatCurrency } from "../../dashboard/component/ReportCard"
import StatCard from "../../dashboard/component/stat-cart"
import { exportLeases } from "../api/lease.api"
import BulkRentIncreaseComponent from "../component/bulk-rent-increase.component"
import ExportLeaseModalComponent from "../component/export-lease-modal.component"
import SendUtilityFeeReminderComponent from "../component/send-utility-fee-remainder.component"
import {
  useLazyCountLeasesQuery,
  useLazyGetLeasesQuery,
  useLazyGetUnitFloorsQuery,
} from "../store/lease.query"
import dayjs from "dayjs"
import BulkManagementFeeIncreaseComponent from "../component/bulk-management-fee-increase.component"

export default function LeasePage() {
  const params = useParams()
  const location = useLocation()
  const [viewMode, setViewMode] = useState<entityViewMode>("list")
  const [filterValue, setFilterValue] = useState<string[]>([])
  const [collection, setCollection] = useState<CollectionQuery>({
    skip: 0,
    top: 10,
    includes: ["tenant", "property", "property.owner", "unit"],
    orderBy: [{ field: "createdAt", direction: "desc" }],
  })
  const [openedExport, { open: openExport, close: closeExport }] =
    useDisclosure()
  const [openedReminder, { open: openReminder, close: closeReminder }] =
    useDisclosure()

  const [
    openedRentIncrease,
    { open: openRentIncrease, close: closeRentIncrease },
  ] = useDisclosure()
  const [
    openedManagementFeeIncrease,
    { open: openManagementFeeIncrease, close: closeManagementFeeIncrease },
  ] = useDisclosure()

  const [getUnitFloors, unitFloors] = useLazyGetUnitFloorsQuery()
  const [getLeases, leases] = useLazyGetLeasesQuery()
  const [countLeases, totalLeases] = useLazyCountLeasesQuery()
  const [countActiveLeases, activeLeases] = useLazyCountLeasesQuery()
  const [countExpiredLeases, expiredLeases] = useLazyCountLeasesQuery()
  const [countTerminatedLeases, terminatedLeases] = useLazyCountLeasesQuery()
  const [countPendingLeases, pendingLeases] = useLazyCountLeasesQuery()

  const [userInfo, userId, ownerInfo] = useCurrentUser()
  const generateFilterOption = (value: string): Filter[][] => {
    if (userInfo.role.key == "super_admin") {
      return [[{ field: "leaseStatus", value: value, operator: "=" }]]
    }

    if (userInfo.role.key == "tenant") {
      return [
        [{ field: "leaseStatus", value: value, operator: "=" }],
        [{ field: "tenantId", value: userId, operator: "=" }],
      ]
    }

    return [
      [{ field: "leaseStatus", value: value, operator: "=" }],
      [
        {
          field: "property.ownerId",
          value: ownerInfo?.parentOwnerId,
          operator: "=",
        },
      ],
    ]
  }

  const filterQuery = (data: string[]) => {
    let filterQueryValue: any[] = []
    const filterMap: { [key: string]: any[] } = {}
    data.forEach((item) => {
      filterMap[JSON.parse(item)?.field] = data.filter(
        (query) => JSON.parse(query)?.field === JSON.parse(item).field,
      )
    })
    // constructs the filter query into array form the grouped object
    Object.keys(filterMap).forEach((key) => {
      filterQueryValue = [
        ...filterQueryValue,
        filterMap[key].map((item) => JSON.parse(item)),
      ]
    })
    return filterQueryValue
  }

  useEffect(() => {
    getUnitFloors({})
  }, [])

  useEffect(() => {
    let filterCollection: Filter[][] = Array.isArray(collection?.filter)
      ? [...(collection?.filter as Filter[][])]
      : []

    if (userInfo.role.key == "super_admin") {
      getLeases({
        ...collection,
        filter: [
          ...filterCollection,
          [{ field: "owner.confidential", value: false, operator: "=" }],
        ],
      })
    } else if (userInfo?.role?.key == "tenant") {
      getLeases({
        ...collection,
        filter: [
          ...filterCollection,
          [
            {
              field: "tenantId",
              value: userId,
              operator: "=",
            },
          ],
        ],
      })
    } else {
      getLeases({
        ...collection,
        filter: [
          ...filterCollection,
          [
            {
              field: "property.ownerId",
              value: ownerInfo?.parentOwnerId,
              operator: "=",
            },
          ],
        ],
      })
    }
  }, [collection])

  useEffect(() => {
    if (userInfo.role.key == "super_admin") {
      countLeases({
        count: true,
        includes: ["property"],
      })
    } else if (userInfo.role.key == "tenant") {
      countLeases({
        count: true,
        includes: ["property"],
        filter: [
          [
            {
              field: "tenantId",
              value: userId,
              operator: "=",
            },
          ],
        ],
      })
    } else {
      countLeases({
        count: true,
        includes: ["property"],
        filter: [
          [
            {
              field: "property.ownerId",
              value: ownerInfo?.parentOwnerId,
              operator: "=",
            },
          ],
        ],
      })
    }
    countActiveLeases({
      count: true,
      includes: ["property"],
      filter: generateFilterOption(LeaseStatus.Active),
    })
    countExpiredLeases({
      count: true,
      includes: ["property"],
      filter: generateFilterOption(LeaseStatus.Expired),
    })
    countTerminatedLeases({
      count: true,
      includes: ["property"],
      filter: generateFilterOption(LeaseStatus.Terminated),
    })
    countPendingLeases({
      count: true,
      includes: ["property"],
      filter: generateFilterOption(LeaseStatus.Pending),
    })
  }, [])

  useEffect(() => {
    if (params?.id !== undefined) {
      setViewMode("detail")
    } else {
      setViewMode("list")
    }
    if (location.pathname === "/leases/archived") {
      setViewMode("detail")
    }
  }, [setViewMode, params?.id, location])

  useEffect(() => {
    // setCollection({...collection, filter: filterQuery(filterValue)})

    if (userInfo.role.key == "super_admin") {
      if (filterValue.length > 0) {
        getLeases({
          ...collection,
          filter: [
            [
              { field: "owner.confidential", value: false, operator: "=" },
              filterQuery(filterValue).flat()[0],
            ],
          ],
        })
      } else {
        getLeases({
          ...collection,
          filter: [
            [
              { field: "owner.confidential", value: false, operator: "=" },
              // filterQuery(filterValue).flat()[0],
            ],
          ],
        })
      }
    } else if (userInfo?.role?.key == "tenant") {
      if (filterValue.length > 0) {
        getLeases({
          ...collection,
          filter: [
            [
              {
                field: "tenantId",
                value: userId,
                operator: "=",
              },
              filterQuery(filterValue).flat()[0],
            ],
          ],
        })
      } else {
        getLeases({
          ...collection,
          filter: [
            [
              {
                field: "tenantId",
                value: userId,
                operator: "=",
              },
              // filterQuery(filterValue).flat()[0],
            ],
          ],
        })
      }
    } else {
      console.log(
        "🚀 ~ LeasePage ~  filterQuery(filterValue).flat(),:",
        filterQuery(filterValue).flat(),
      )
      console.log("🚀 ~ LeasePage ~ filterValue:", [
        [
          {
            field: "property.ownerId",
            value: ownerInfo?.parentOwnerId,
            operator: "=",
          },
        ],
        filterQuery(filterValue).flat(),
      ])

      if (filterValue.length > 0) {
        getLeases({
          ...collection,
          filter: [
            [
              {
                field: "property.ownerId",
                value: ownerInfo?.parentOwnerId,
                operator: "=",
              },
            ],
            ...filterQuery(filterValue),
          ],
        })
      } else {
        getLeases({
          ...collection,
          filter: [
            [
              {
                field: "property.ownerId",
                value: ownerInfo?.parentOwnerId,
                operator: "=",
              },
              // filterQuery(filterValue).flat()[0],
            ],
          ],
        })
      }
    }
  }, [filterValue])

  const data: Lease[] | undefined = leases.data?.data

  const config: EntityConfig<Lease> = {
    primaryColumn: {
      key: "name",
      name: t("name"),
      render: (value) =>
        value.name && value.name.length > 20 ? (
          <Tooltip label={value?.name} withArrow className="z-50">
            <Text size="sm" className={value?.deletedAt ? "text-danger" : ""}>
              {`${value?.name?.substring(0, 20)} ...`}
            </Text>
          </Tooltip>
        ) : (
          value?.name
        ),
    },
    detailUrl: "detail",
    rootUrl: "/lease",
    identity: "id",
    name: "name",
    showDetail:
      userInfo?.role?.key === "super_admin" ||
      userInfo?.role?.key === "admin" ||
      userInfo?.role?.key === "tenant" ||
      userInfo?.role?.key === "owner",
    visibleColumn: [
      {
        key: "name",
        name: t("name"),
        render: (value) =>
          value.name && value.name.length > 20 ? (
            <Tooltip label={value?.name} withArrow className="z-50">
              <Text size="sm" className={value?.deletedAt ? "text-danger" : ""}>
                {`${value?.name?.substring(0, 20)} ...`}
              </Text>
            </Tooltip>
          ) : (
            value?.name
          ),
      },
      {
        key: "tenant",
        name: `${t("tenant", { count: 1 })} (${t("tinNumber")})`,
        render: (value: Lease) => {
          return `${value?.tenant?.name} ${
            value?.tenant?.tinNumber ? "(" + value?.tenant?.tinNumber + ")" : ""
          }`
        },
      },
      {
        key: "unit",
        name: "Unit",
        render: (value: Lease) => {
          return value?.unit?.name
        },
      },
      // {
      //   key: 'endDate', name: 'End Date', render: (value: Lease) => {
      //     return dateFormat(value?.endDate, { day: true, weekday: true, hour: false, minute: false })
      //   }
      // },
      {
        key: "totalRentAmount",
        name: t("rentAmount"),
        render(value: Lease) {
          return `${formatCurrency(value?.rentAmount as number)} ETB`
        },
      },
      {
        key: "rentAmount",
        name: t("totalRentAmount"),
        render(value: Lease) {
          return `${formatCurrency(
            value?.totalRentAmount ?? (0 as number),
          )} ETB`
        },
      },
      {
        key: "utilityFee",
        name: t("administrativeFee"),
        render(value: Lease) {
          return `${formatCurrency(value?.utilityFee as number)} ETB`
        },
      },
      // {key: 'securityDeposit', name: 'Security Deposit'},
      { key: "paymentFrequency", name: t("paymentFrequency") },
      // { key: 'leaseType', name: 'Lease Type' },
      // {key: 'lateFee', name: 'Late Fee'},
      // {key: 'terminationNotice', name: 'Termination Notice'},
      { key: "leaseStatus", name: t("leaseStatus") },
      {
        key: "paymentDueDate",
        name: t("paymentDueDate"),
        render: (value: Lease) => {
          return dateFormat(value?.paymentDueDate, {
            day: true,
            weekday: true,
            hour: false,
            minute: false,
          })
        },
      },
      {
        key: "difference",
        name: "Remaining Days",
        render(value: Lease) {
          const remainingDays = dayjs(value?.paymentDueDate).diff(
            dayjs(),
            "day",
          )
          const sign = remainingDays > 0 ? "+" : "-"

          let displayValue = ""
          if (Math.abs(remainingDays) < 30) {
            // Show in days
            displayValue = `${Math.abs(remainingDays)} day${
              Math.abs(remainingDays) === 1 ? "" : "s"
            }`
          } else {
            // Show in months
            const remainingMonths = dayjs(value?.paymentDueDate).diff(
              dayjs(),
              "month",
            )
            displayValue = `${Math.abs(remainingMonths)} month${
              Math.abs(remainingMonths) === 1 ? "" : "s"
            }`
          }

          return sign === "+"
            ? `${displayValue} left`
            : `${displayValue} overdue`
        },
      },
      {
        key: "createdAt",
        name: t("registeredAt"),
        render(value) {
          return dateFormat(value.createdAt)
        },
      },
    ],
    filter: [
      [{ field: "status", name: t("status"), operator: "=", value: "active" }],
    ],
  }

  const filter: Filter[][] = [
    [
      {
        field: "leaseStatus",
        value: LeaseStatus.Active,
        name: LeaseStatus.Active,
        operator: "=",
      },
      {
        field: "leaseStatus",
        value: LeaseStatus.Completed,
        name: LeaseStatus.Completed,
        operator: "=",
      },
      {
        field: "leaseStatus",
        value: LeaseStatus.Expired,
        name: LeaseStatus.Expired,
        operator: "=",
      },
      {
        field: "leaseStatus",
        value: LeaseStatus.Pending,
        name: LeaseStatus.Pending,
        operator: "=",
      },
      {
        field: "leaseStatus",
        value: LeaseStatus.Terminated,
        name: LeaseStatus.Terminated,
        operator: "=",
      },
    ],
    unitFloors?.data?.map((floor: any) => ({
      field: "unit.floor",
      value: floor,
      name: floor,
      operator: "=",
    })),
  ]

  // filter.push([
  //   {field: 'status', value:LeaseStatus.Active, name: LeaseStatus.Active, operator: "="},
  //   {field: 'status', value:LeaseStatus.Completed, name: LeaseStatus.Completed, operator: "="},
  //   {field: 'status', value:LeaseStatus.Expired, name: LeaseStatus.Expired, operator: "="},
  //   {field: 'status', value:LeaseStatus.Pending, name: LeaseStatus.Pending, operator: "="},
  //   {field: 'status', value:LeaseStatus.Terminated, name: LeaseStatus.Terminated, operator: "="},
  // ])

  return (
    <main className="max-w-7xl mx-auto py-6 px-4 lg:px-8 mt-16">
      <LoadingOverlay visible={leases.isFetching} />

      <motion.div
        className="hidden md:grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <StatCard
          name={t("totalLease")}
          icon={Package}
          value={totalLeases?.data?.count}
          color="#6366F1"
        />
        <StatCard
          name={t("pendingLease")}
          icon={DollarSign}
          value={pendingLeases?.data?.count}
          color="#10B981"
        />
        <StatCard
          name={t("activeLease")}
          icon={TrendingUp}
          value={activeLeases?.data?.count}
          color="#8B5CF6"
        />
        <StatCard
          name={t("expiredLease")}
          icon={AlertTriangle}
          value={expiredLeases?.data?.count}
          color="#EC4899"
        />
        <StatCard
          name={t("terminatedLease")}
          icon={XCircle}
          value={terminatedLeases?.data?.count}
          color="#EC4999"
        />
      </motion.div>

      <EntityList
        viewMode={viewMode}
        showNewButton={
          userInfo?.role?.key === "super_admin" ||
          userInfo?.role?.key === "admin" ||
          userInfo?.role?.key === "owner"
        }
        header={
          // <>
          <div className="flex  justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-100">
              {t("lease", { count: 2 })}
            </h2>
            <div className="flex justify-end gap-2 ">
              {/* <div className="relative"> */}
              <input
                type="text"
                placeholder={t("searchHere")}
                className="bg-gray-700 w-[50%] h-7 text-white placeholder-gray-400 rounded-lg pl-2 md:pl-10 pr-1 md:pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyUp={debounce(
                  (event: any) =>
                    setCollection({
                      ...collection,
                      search: event.target.value,
                      searchFrom: ["name", "tenant.tinNumber", "unit.name"],
                    }),
                  1000,
                )}
              />

              {/* <Search
                  className="absolute left-3 top-2.5 text-gray-400"
                  size={18}
                />
              </div> */}

              <Menu trigger="click" openDelay={100} closeDelay={400}>
                <Menu.Target>
                  <Tooltip label="Actions" className="cursor-pointer">
                    <Sliders />
                  </Tooltip>
                </Menu.Target>

                <Menu.Dropdown>
                  {/* <Menu.Label>
                    {showSelector && (
                      <Tooltip label="Selector">
                        <Checkbox
                          size="lg"
                          className=" rounded-l"
                          icon={CheckboxIcon}
                          onClick={() => setCheck(!check)}
                          indeterminate
                        />
                      </Tooltip>
                    )}
                  </Menu.Label> */}
                  <Menu.Item>
                    <div className="flex justify-center items-center">
                      <Checkbox
                        size="sm"
                        onChange={(e) =>
                          setCollection({
                            ...collection,
                            withArchived: e.currentTarget.checked
                              ? true
                              : false,
                          })
                        }
                        label={
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {t("showArchived")}
                          </span>
                        }
                      />
                    </div>
                  </Menu.Item>

                  {(userInfo?.role?.key === "super_admin" ||
                    userInfo?.role?.key === "owner") && (
                    <>
                      <Menu.Item>
                        <Button
                          classNames={{ label: "flex space-x-2 text-white" }}
                          className="bg-primary dark:bg-dark_primary w-full"
                          size="xs"
                          variant="filled"
                          onClick={openRentIncrease}
                        >
                          <span className="text-white">
                            <TrendingUp />
                          </span>
                          <span>{t("rentIncrease")}</span>
                        </Button>
                      </Menu.Item>
                      <Menu.Item>
                        <Button
                          classNames={{ label: "flex space-x-2 text-white" }}
                          className="bg-primary dark:bg-dark_primary w-full"
                          size="xs"
                          variant="filled"
                          onClick={openManagementFeeIncrease}
                        >
                          <span className="text-white">
                            <TrendingUp />
                          </span>
                          <span>{t("managementFeeIncrease")}</span>
                        </Button>
                      </Menu.Item>
                      <Menu.Item>
                        <Button
                          classNames={{ label: "flex space-x-2 text-white" }}
                          className="bg-primary dark:bg-dark_primary"
                          size="xs"
                          variant="filled"
                          onClick={openReminder}
                        >
                          {/* <span className="text-white">
                              <TrendingUp />
                            </span> */}
                          <span>{"Send Management Fee reminder"}</span>
                        </Button>
                      </Menu.Item>
                      <Menu.Item>
                        <Link
                          to={`/lease/new`}
                          state={{ id: location?.state?.id }}
                        >
                          <Button
                            classNames={{
                              label: "flex  space-x-2 text-white",
                            }}
                            className="bg-primary dark:bg-dark_primary w-full"
                            size="xs"
                            variant="filled"
                          >
                            <span className="text-white">
                              <Plus />
                            </span>
                            <span>{t("new")}</span>
                          </Button>
                        </Link>
                      </Menu.Item>
                      <Menu.Item>
                        <Button
                          classNames={{ label: "flex space-x-2 text-white" }}
                          className="bg-primary dark:bg-dark_primary w-full"
                          size="xs"
                          variant="filled"
                          onClick={openExport}
                        >
                          <span className="text-white">
                            <FileSpreadsheet />
                          </span>
                          <span>{t("export")}</span>
                        </Button>
                      </Menu.Item>
                    </>
                  )}
                </Menu.Dropdown>
              </Menu>

              {unitFloors?.data?.length > 0 && (
                <Menu
                  withArrow
                  position="left-start"
                  closeOnItemClick={false}
                  classNames={{ itemLabel: "gap-2" }}
                >
                  <Menu.Target>
                    <Tooltip label="Filter">
                      <Button
                        className={` dark:bg-dark_primary dark:border-none dark:text-white`}
                        classNames={{
                          label: "flex space-x-2  text-gray-700",
                        }}
                        size="xs"
                        variant="default"
                      >
                        <span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="fill-current h-4 dark:text-white"
                            viewBox="0 0 26 26"
                          >
                            <path d="M2 0C0.894531 0 0 0.894531 0 2L9 12L9 21.40625C9 22.511719 9.894531 23.777344 11 24.21875L15 25.8125C15.261719 25.917969 15.519531 25.96875 15.75 25.96875C16.488281 25.96875 17 25.464844 17 24.625L17 12L26 2C26 0.894531 25.105469 0 24 0 Z M 2.6875 2L23.3125 2L16.125 10L9.90625 10 Z M 11 12L15 12L15 23.65625L11.75 22.34375C11.367188 22.191406 11 21.648438 11 21.40625Z" />
                          </svg>
                        </span>
                      </Button>
                    </Tooltip>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Label>{t("filterBy")}</Menu.Label>
                    <Menu.Item>
                      <Checkbox.Group
                        size="xs"
                        withAsterisk
                        defaultValue={filterValue}
                        value={filterValue}
                        onChange={(data: any) => {
                          setFilterValue(data)
                        }}
                      >
                        {filter?.map((item, idx) => (
                          // <>
                          <div key={idx} className="flex-col space-y-2">
                            {item &&
                              Array.isArray(item) &&
                              item.map((filter, index) => (
                                <Checkbox
                                  key={index}
                                  value={JSON.stringify(filter)}
                                  label={filter.name}
                                />
                              ))}
                            {filter?.length !== idx + 1 && (
                              <Divider
                                label={t("and")}
                                labelPosition="center"
                                my={"sm"}
                              />
                            )}
                          </div>
                          // </>
                        ))}
                      </Checkbox.Group>
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )}
            </div>
          </div>
          // </>
        }
        showExport={true}
        title={t("lease", { count: 2 })}
        total={leases?.data?.count}
        itemsLoading={leases.isFetching}
        config={config}
        items={data}
        initialPage={1}
        defaultPageSize={10}
        pageSize={[10, 15, 20]}
        collectionQuery={collection}
        onArchivedChecked={(e) =>
          setCollection({
            ...collection,
            withArchived: e,
          })
        }
        onPaginationChange={(skip: number, top: number) => {
          if (collection.skip !== skip || collection.top !== top) {
            if (filterValue.length > 0) {
              setCollection({
                ...collection,
                skip: skip,
                top: top,
                filter: filterQuery(filterValue),
              })
            } else {
              setCollection({
                ...collection,
                skip: skip,
                top: top,
                // filter: filterQuery(filterValue)
              })
            }
          }
        }}
        onSearch={(data: any) => {
          setCollection({
            ...collection,
            search: data,
            searchFrom: ["name", "tenant.tinNumber", "unit.name"],
          })
        }}
        onFilterChange={(data: any) => {
          if (collection?.filter || data.length > 0) {
            setCollection({ ...collection, filter: data })
          }
        }}
        onOrder={(data: Order) =>
          setCollection({ ...collection, orderBy: [data] })
        }
        onExport={openExport}
      />

      <ExportLeaseModalComponent
        open={openExport}
        opened={openedExport}
        close={closeExport}
        filename={"leases.xlsx"}
        filteringVariables={[
          [
            {
              field: "leaseStatus",
              value: LeaseStatus.Active,
              name: t("active"),
              operator: "=",
            },
            {
              field: "leaseStatus",
              value: LeaseStatus.Expired,
              name: t("expired"),
              operator: "=",
            },
            {
              field: "leaseStatus",
              value: LeaseStatus.Pending,
              name: t("pending"),
              operator: "=",
            },
            {
              field: "leaseStatus",
              value: LeaseStatus.Terminated,
              name: t("terminated"),
              operator: "=",
            },
          ],
          // [
          //   {
          //     field: "leaseType",
          //     value: LeaseType.FixedTerm,
          //     name: LeaseType.FixedTerm,
          //     operator: "=",
          //   },
          //   {
          //     field: "leaseType",
          //     value: LeaseType.MonthToMonth,
          //     name: LeaseType.MonthToMonth,
          //     operator: "=",
          //   },
          //   {
          //     field: "leaseType",
          //     value: LeaseType.Sublease,
          //     name: LeaseType.Sublease,
          //     operator: "=",
          //   },
          // ],
          [
            {
              field: "paymentFrequency",
              value: PaymentFrequency.Monthly,
              name: PaymentFrequency.Monthly,
              operator: "=",
            },

            {
              field: "paymentFrequency",
              value: PaymentFrequency.Quarterly,
              name: PaymentFrequency.Quarterly,
              operator: "=",
            },

            {
              field: "paymentFrequency",
              value: PaymentFrequency.SixMonths,
              name: PaymentFrequency.SixMonths,
              operator: "=",
            },
            {
              field: "paymentFrequency",
              value: PaymentFrequency.Annually,
              name: PaymentFrequency.Annually,
              operator: "=",
            },
          ],
        ]}
        exportFunction={exportLeases}
      />

      <BulkRentIncreaseComponent
        open={openRentIncrease}
        opened={openedRentIncrease}
        close={closeRentIncrease}
      />
      <BulkManagementFeeIncreaseComponent
        close={closeManagementFeeIncrease}
        open={openManagementFeeIncrease}
        opened={openedManagementFeeIncrease}
      />
      <SendUtilityFeeReminderComponent
        open={openReminder}
        opened={openedReminder}
        close={closeReminder}
      />
    </main>
  )
}
